<template>
  <div id="app">
    <div v-if="loading" class="overlay">
      <loading-icon></loading-icon>
    </div>
    <div v-else>
      <nav-menu v-show="showMenu" :userAdvertisers="userAdvertisers" :currentAdvertiser="currentAdvertiser"></nav-menu>
      <alert-bubbles :currentAdvertiser="currentAdvertiser"></alert-bubbles>
      <router-view :currentAdvertiser="currentAdvertiser"/>
    </div>
    <icons></icons>
  </div>
</template>

<script>
  import NavMenu from './components/Shared/NavMenu'
  import Icons from './components/Shared/Icons'
  import router from './router/router'
  import LoadingIcon from './components/Shared/LoadingIcon'
  import AlertBubbles from './components/Shared/AlertBubbles'
  import { brandsAPI } from './services/api/brands'

  export default {
    components: {
      AlertBubbles,
      Icons,
      NavMenu,
      LoadingIcon
    },

    data() {
      return {
        advertiserIdFromParams: null,
        loading: true,
        userAdvertisers: null,
        currentAdvertiser: {
          id: localStorage.getItem('a_id'),
          name: null
        }
      }
    },

    methods: {
      authSetup() {
        const token = this.urlParams('token'),
            userId = this.urlParams('user_id'),
            hasUrlParams = !!token && !!userId,
            hasStoredParams = !!localStorage.getItem('t') && !!localStorage.getItem('u')
        this.advertiserIdFromParams = this.urlParams('advertiser_id')

        if (hasUrlParams) {
          localStorage.setItem('t', token)
          localStorage.setItem('u', userId)
          this.$store.dispatch('UPDATE_STATE_ITEM', { stateKey: 'userId', stateItem: userId })
          window.history.replaceState({}, '', `/#/`)
          this.getAdvertisers()
        } else if (hasStoredParams) {
          this.getAdvertisers()
        } else {
          router.push({path:'/logout'})
        }
      },

      configureCurrentAdvertiser(userAdvertiser) {
        const advertiserAttributes = ['id', 'name', 'time_zone', 'created_at', 'latest_ad_start', 'admin', 'access_to', 'key_terms']
        advertiserAttributes.forEach((att) => this.$set(this.currentAdvertiser, att, userAdvertiser[att]))
      },

      async getAdvertisers() {
        try {
          const data = await brandsAPI.getUserAdvertisers()
          this.userAdvertisers = data
          this.setCurrentAdvertiser()
          await this.$store.dispatch('FETCH_SUGGESTED_TAGS', { advertiserID: this.currentAdvertiser.id })
          await this.$store.dispatch('FETCH_MERCHANT_REQUESTS', { advertiserID: this.currentAdvertiser.id })
          this.loading = false
        } catch(error) {
          localStorage.clear()
          this.$router.push({ path: '/logout' })
        }
      },

      selectBrandToSet() {
        if (!this.userAdvertisers) { return }
        const brandIdFromParam = this.advertiserIdFromParams,
          storedBrandId = localStorage.getItem('a_id'),
          brandId = brandIdFromParam || storedBrandId,
          brandToSet = !brandId ? this.userAdvertisers[0] : this.userAdvertisers.filter(({ id }) => id == brandId)[0],
          shouldUpdateBrandToSet = !brandId || (brandIdFromParam && brandIdFromParam !== storedBrandId)

        shouldUpdateBrandToSet && localStorage.setItem('a_id', brandToSet.id)

        return brandToSet
      },

      setCurrentAdvertiser() {
        const brandToSet = this.selectBrandToSet()
        this.configureCurrentAdvertiser(brandToSet)
        this.$store.commit('SET_BRAND', { brand: brandToSet })
      },

      urlParams(selectedParam) {
        const urlParams = location.search;
        let params = {};
        if (urlParams) {
          urlParams.substr(1).split("&").forEach(function (item) {
            let key_value = item.split("=");
            if (key_value[0] && key_value[1]) {
              params[key_value[0]] = key_value[1].replace(/[^\w\s]/gi, '')
            }
          })
          return params[selectedParam]
        }
      },
    },

    created() {
      this.authSetup()
    },

    computed: {
      showMenu() {
        return this.$route.name !== 'AdEdit'
      },
    },
  }
</script>

<style lang="sass">
@import './assets/sass/reset'
@import './assets/sass/colors'
@font-face
  font-family: 'AileronThin'
  src: url('./assets/fonts/aileron/Aileron-Thin.eot')
  src: url('./assets/fonts/aileron/Aileron-Thin.eot?#iefix') format('embedded-opentype'), url('./assets/fonts/aileron/Aileron-Thin.woff') format('woff'), url('./assets/fonts/aileron/Aileron-Thin.ttf') format('truetype')
  font-weight: 100
  font-style: normal

@font-face
  font-family: 'AileronLight'
  src: url('./assets/fonts/aileron/aileron-light-webfont.woff2') format('woff2'), url('./assets/fonts/aileron/aileron-light-webfont.woff') format('woff'), url('./assets/fonts/aileron/aileron-light-webfont.ttf') format('truetype')
  font-weight: normal
  font-style: normal

/* Global Styles */
body, input
  font-family: "AileronThin", Helvetica, Roboto, Arial, sans-serif

h1
  font-size: 2em

h2
  font-size: 1.5em

h3
  font-size: 1.17em

h4
  font-size: 1em

h5
  font-size: .83em

h6
  font-size: .67em

.page-title
  color: $title-color

p, label
  color: $title-color
  font-size: 1.25rem

.button
  background: $primary-color
  border: 0
  border-radius: 14px
  color: #ffffff
  cursor: pointer
  font-family: 'AileronThin'
  font-size: 1.25rem
  padding: 0.5rem 2rem
  text-decoration: none
  transition: background 0.25s ease-out

  &:hover
    background: $tertiary-color

.button.secondary
  border: 1px solid $tertiary-color
  background-color: #ffffff
  color: $tertiary-color
  &:hover
    background-color: $tertiary-color
    color: #ffffff
  &:active, &:focus
    outline: none

.link
  color: $primary-color
  transition: color 0.25s ease-out

  &:hover
    color: $tertiary-color

.hide
  display: none

input, textarea
  border-radius: 32px
  border: 1px solid $primary-color
  font-family: "AileronThin", Helvetica, Roboto, Arial, sans-serif
  font-size: 1.25rem
  margin-bottom: 1.25rem
  outline: none
  padding: .5rem 1rem

input:disabled
  cursor: not-allowed

select
  border-radius: 6px
  border: 1px solid $primary-color
  box-shadow: none
  background: #fff
  padding: 0.5rem
  -webkit-appearance: none
  -moz-appearance: none
  appearance: none
  font-size: 1rem
  font-family: "AileronThin", Helvetica, Roboto, Arial, sans-serif

.loading-icon
  width: 50px
  margin: 3rem auto

  svg
    fill: $primary-color

.overlay
  align-items: center
  background-color: rgba(0, 0, 0, 0.5)
  bottom: 0
  display: flex
  justify-content: center
  left: 0
  position: fixed
  right: 0
  top: 0
  z-index: 9

  .loading-icon
    width: 100px
    svg
      fill: #fff

.header
  color: $title-color
  font-size: 1.2rem
  font-weight: normal

.sub-header
  margin-bottom: 1rem
  font-weight: normal
  font-size: 1.15rem

.flip-scrollbar-parent, .flip-scrollbar-child
  transform: scaleX(-1)

.checkmark
  align-items: center
  background-color: $primary-color
  border-radius: 50%
  display: none
  height: 22px
  justify-content: center
  margin-left: 1rem
  width: 22px

.check
  border-bottom: 1px solid #fff
  border-right: 1px solid #fff
  border-radius: 1px
  display: block
  height: 11px
  margin-top: -4px
  transform: rotate(45deg)
  width: 5px

.delete-link
  cursor: pointer
  color: $negative-color
  font-size: .9rem
  &:hover
    color: $negative-hover

.error-message
  color: $negative-hover
  font-size: .9rem

.empty-response
  margin: 1rem

.divider
  display: block
  width: 80%
  margin: 1rem auto
  padding-left: 0
  border-top: 1px solid $primary-transparent

.divider-vertical
  border-right: 1px solid $primary-transparent

.hide-for-small
  @media only screen and (max-width: 40em)
    display: none

.hide-up-to-tablet
  @media only screen and (max-width: 768px)
    display: none

.hide-down-to-tablet
  @media only screen and (min-width: 768px)
    display: none

.decrease-columns-to-mobile
  @media only screen and (max-width: 425px)
    grid-template-columns: repeat(2, 1fr)
</style>
